import {
	Button,
	Col,
	DatePicker,
	Form,
	Input,
	Row,
	Select,
	Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { InternshipEngagementType } from "../../../Api";
import {
	userYearsBachelor,
	professorInternshipTypes,
	dateFormatList,
} from "../../../utils/constants";
import { useFacultySettingsFormController } from "./FacultySettingsForm.controller";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "../../../utils/dayjs-setup";
import { useState } from "react";
import { theme } from "../../../theme";

const { Option } = Select;

export const FacultySettingsForm = () => {
	const { t } = useTranslation();
	const { state, actions } = useFacultySettingsFormController();
	const [forceUpdate, setForceUpdate] = useState(0);

	return (
		<div className="container mx-auto my-8">
			<Form
				form={state.form}
				onFinish={actions.onFinish}
				onValuesChange={() => setForceUpdate((prev) => prev + 1)}
				style={{ width: "90%", margin: "auto" }}
				layout={theme.layout}
			>
				<Typography style={{ textAlign: "left" }}>
					{t("facultySettings.setPracticePeriods")}:
				</Typography>

				<Form.List name="practiceInternshipPeriods">
					{(fields, { add, remove }, { errors }) => (
						<>
							{fields.map(({ key, name, ...restField }) => (
								<Row key={key} gutter={8}>
									<Col span={3}>
										<Form.Item
											{...restField}
											name={[name, "practiceYear"]}
											rules={[
												{
													required: true,
													message: t("facultySettings.missingYear"),
												},
											]}
										>
											<Select disabled={!state.editEnabled}>
												{userYearsBachelor.map((year) => (
													<Option key={"year" + year} value={year}>
														{year}
													</Option>
												))}
											</Select>
										</Form.Item>
									</Col>
									<Col span={4}>
										<Form.Item
											{...restField}
											name={[name, "studyProgramId"]}
											rules={[
												{
													required: true,
													message: t("facultySettings.missingStudyProgram"),
												},
											]}
										>
											<Select
												key={`${name}-${state.form.getFieldValue([
													"practiceInternshipPeriods",
													name,
													"practiceYear",
												])}-${state.form.getFieldValue([
													"practiceInternshipPeriods",
													name,
													"studyProgramId",
												])}-${forceUpdate}`}
												disabled={!state.editEnabled}
												placeholder={
													state.studyPrograms
														? undefined
														: t("facultySettings.loadingStudyPrograms")
												}
											>
												{(() => {
													const currentPeriods =
														state.form.getFieldValue(
															"practiceInternshipPeriods"
														) || [];
													const currentYear = state.form.getFieldValue([
														"practiceInternshipPeriods",
														name,
														"practiceYear",
													]);
													const currentStudyProgram = state.form.getFieldValue([
														"practiceInternshipPeriods",
														name,
														"studyProgramId",
													]);

													if (!currentYear) {
														return state.studyPrograms?.map((studyProgram) => (
															<Option
																key={studyProgram.id}
																value={studyProgram.id}
															>
																{studyProgram.name}
															</Option>
														));
													}

													const selectedCombinations = currentPeriods
														.map((period: any, index: number) => ({
															studyProgram: period?.studyProgramId,
															year: period?.practiceYear,
															index,
														}))
														.filter(
															(combination: any) =>
																combination.studyProgram &&
																combination.year &&
																!(
																	combination.studyProgram ===
																		currentStudyProgram &&
																	combination.year === currentYear &&
																	combination.index === name
																)
														);

													const availableStudyPrograms =
														state.studyPrograms?.filter((studyProgram) => {
															return !selectedCombinations.some(
																(combination: any) =>
																	combination.studyProgram ===
																		studyProgram.id &&
																	combination.year === currentYear
															);
														});

													return availableStudyPrograms?.map((studyProgram) => (
														<Option
															key={studyProgram.id}
															value={studyProgram.id}
														>
															{studyProgram.name}
														</Option>
													));
												})()}
											</Select>
										</Form.Item>
									</Col>
									<Col span={5}>
										<Form.Item
											{...restField}
											name={[name, "practiceEvaluationStart"]}
											rules={[
												{
													required: true,
													message: t("facultySettings.missingStartDate"),
												},
											]}
										>
											<DatePicker
												format={dateFormatList}
												style={{ width: "100%" }}
												value={dayjs(
													state.form.getFieldValue(
														"practiceInternshipPeriods.practiceEvaluationStart"
													)
												)}
												disabled={!state.editEnabled}
											/>
										</Form.Item>
									</Col>
									<Col span={5}>
										<Form.Item
											{...restField}
											name={[name, "practiceEvaluationEnd"]}
											rules={[
												{
													required: true,
													message: t("facultySettings.missingEndDate"),
												},
											]}
										>
											<DatePicker
												format={dateFormatList}
												style={{ width: "100%" }}
												disabled={!state.editEnabled}
											/>
										</Form.Item>
									</Col>
									<Col span={3}>
										<Form.Item
											name={[name, "practiceNumberOfHours"]}
											rules={[
												{
													required: true,
													message: t("facultySettings.missingNumberOfHours"),
												},
											]}
										>
											<Input
												disabled={!state.editEnabled}
												placeholder={t(
													"facultySettings.numberOfHoursPlaceholder"
												)}
											></Input>
										</Form.Item>
									</Col>
									<Col span={2}>
										<Form.Item
											name={[name, "practiceECTS"]}
											rules={[
												{
													required: true,
													message: t("facultySettings.missingNumberOfCredits"),
												},
											]}
										>
											<Input
												disabled={!state.editEnabled}
												placeholder={t(
													"facultySettings.numberOfCreditsPlaceholder"
												)}
											></Input>
										</Form.Item>
									</Col>
									<Col span={1}>
										{state.editEnabled && (
											<MinusCircleOutlined onClick={() => remove(name)} />
										)}
									</Col>
								</Row>
							))}
							{state.editEnabled && (
								<Row gutter={8}>
									<Col span={23}>
										<Form.Item>
											<Button
												type="dashed"
												onClick={() => add()}
												block
												icon={<PlusOutlined />}
											>
												{t("facultySettings.addPracticeLimit")}
											</Button>
										</Form.Item>
									</Col>
								</Row>
							)}
						</>
					)}
				</Form.List>

				<Typography style={{ textAlign: "left" }}>
					{t("facultySettings.setInternshipLimits")}:
				</Typography>

				<Form.List name="internshipLimits">
					{(fields, { add, remove }, { errors }) => (
						<>
							{fields.length > 0 && (
								<Row gutter={8} style={{ marginBottom: "8px" }}>
									<Col span={8}>
										<Typography.Text strong>
											{t("facultySettings.internshipTypeLabel")}
										</Typography.Text>
									</Col>
									<Col span={7}>
										<Typography.Text strong>
											{t("facultySettings.maximumApplicantsLabel")}
										</Typography.Text>
									</Col>
									<Col span={7}>
										<Typography.Text strong>
											{t("facultySettings.companyMaximumApplicantsLabel")}
										</Typography.Text>
									</Col>
									<Col span={1}></Col>
								</Row>
							)}
							{fields.map(({ key, name, ...restField }) => (
								<div>
									<Row gutter={8}>
										<Col span={8}>
											<Form.Item
												{...restField}
												name={[name, "internshipType"]}
												rules={[
													{
														required: true,
														message: t("facultySettings.missingOfferType"),
													},
												]}
											>
												<Select
													disabled={!state.editEnabled}
													key={`internshipLimit-internshipType-${name}-${forceUpdate}`}
												>
													{(() => {
														const limits =
															state.form.getFieldValue("internshipLimits") ||
															[];
														const currentRowType = state.form.getFieldValue([
															"internshipLimits",
															name,
															"internshipType",
														]);
														const practiceInternshipTakenOnOtherRow =
															limits.some(
																(
																	row: {
																		internshipType?: InternshipEngagementType;
																	},
																	index: number
																) =>
																	index !== name &&
																	row?.internshipType ===
																		InternshipEngagementType.Internship
															);

														return professorInternshipTypes
															.filter((type) => {
																if (
																	type !== InternshipEngagementType.Internship
																) {
																	return true;
																}
																if (
																	currentRowType ===
																	InternshipEngagementType.Internship
																) {
																	return true;
																}
																return !practiceInternshipTakenOnOtherRow;
															})
															.map((type) => (
																<Option key={"type" + type} value={type}>
																	{t("internships.filters." + type)}
																</Option>
															));
													})()}
												</Select>
											</Form.Item>
										</Col>
										<Col span={7}>
											<Form.Item
												{...restField}
												name={[name, "maximumApplicants"]}
												initialValue={0}
												rules={[
													{
														required: true,
														message: t(
															"facultySettings.missingApplicantsLimit"
														),
													},
												]}
											>
												<Input
													disabled={!state.editEnabled}
													placeholder={t(
														"facultySettings.maximumApplicantsPlaceholder"
													)}
												></Input>
											</Form.Item>
										</Col>
										<Col span={7}>
											<Form.Item
												{...restField}
												name={[name, "companyMaximumApplicants"]}
												initialValue={0}
												rules={[
													{
														required: true,
														message: t(
															"facultySettings.missingCompanyApplicantsLimit"
														),
													},
												]}
											>
												<Input
													disabled={!state.editEnabled}
													placeholder={t(
														"facultySettings.companyMaximumApplicantsPlaceholder"
													)}
												></Input>
											</Form.Item>
										</Col>
										<Col span={1}>
											{state.editEnabled && (
												<MinusCircleOutlined onClick={() => remove(name)} />
											)}
										</Col>
									</Row>
								</div>
							))}
							{state.editEnabled && (
								<Row gutter={8}>
									<Col span={23}>
										<Form.Item>
											<Button
												type="dashed"
												onClick={() => add()}
												block
												icon={<PlusOutlined />}
											>
												{t("facultySettings.addApplicantsLimit")}
											</Button>
										</Form.Item>
									</Col>
								</Row>
							)}
						</>
					)}
				</Form.List>

				<Row gutter={8}>
					<Col span={23}>
						<Form.Item
							label={t("facultySettings.decisionNumber")}
							name="decisionNumber"
						>
							<Input
								disabled={!state.editEnabled}
								placeholder={t("facultySettings.decisionNumberPlaceholder")}
							></Input>
						</Form.Item>
					</Col>
				</Row>

				<Typography
					style={{ textAlign: "left", marginBottom: "1rem", marginTop: "4px" }}
				>
					{t("facultySettings.facultyContactInformation")}:
				</Typography>
				<Row gutter={8}>
					<Col span={23}>
						<Form.Item
							label={t("facultySettings.contactPersonName")}
							name="facultyContactPersonName"
						>
							<Input
								disabled={!state.editEnabled}
								placeholder={t("facultySettings.contactPersonName")}
							></Input>
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={8}>
					<Col span={23}>
						<Form.Item label={t("facultySettings.email")} name="facultyEmail">
							<Input
								disabled={!state.editEnabled}
								placeholder={t("facultySettings.email")}
							></Input>
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={8}>
					<Col span={23}>
						<Form.Item label={t("facultySettings.phone")} name="facultyPhone">
							<Input
								disabled={!state.editEnabled}
								placeholder={t("facultySettings.phone")}
							></Input>
						</Form.Item>
					</Col>
				</Row>

				{state.editEnabled && (
					<div className="space-x-2">
						<Button htmlType="submit" type={"primary"}>
							{t("facultySettings.save")}
						</Button>

						<Button onClick={actions.handleCancel}>
							{t("facultySettings.cancel")}
						</Button>
					</div>
				)}
				{!state.editEnabled && (
					<Button onClick={actions.handleEdit} type={"primary"}>
						{t("facultySettings.edit")}
					</Button>
				)}
			</Form>
		</div>
	);
};
